import {
  useModalContext,
  WSButton,
  WSCopyText,
  WSIcon,
  WSModal,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { useUserId } from "../../query/hooks/helpers";
import { useUserProfile } from "../../query/users/queries";
import { track } from "../../utils/analytics";
import styles from "./ModalReferrals.module.scss";

export const REFERRALS_MODAL = "REFERRALS_MODAL";

const getMailtoBody = (tag: string) =>
  encodeURI(`Join me on Wingspan, the all-in-one platform for the self-employed.

✅ Easily invoice your clients
✅ Pay your taxes and get maximum returns
✅ Find benefits within your budget

https://www.wingspan.app/?rc=${tag}`);

const schema = Yup.string()
  .email("Please, enter valid email")
  .required("Email is required");

type ModalContentProps = {
  customReferralCode?: string;
};

export const ModalContent: React.FC<ModalContentProps> = ({
  customReferralCode
}) => {
  const { closeModal } = useModalContext();

  const userId = useUserId();
  const qUserData = useUserProfile(userId);

  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    schema
      .validate(email)
      .then(() => {
        setError(null);
      })
      .catch(err => {
        setError(err.errors[0]);
      });
  }, [email]);

  useEffect(() => {
    track("Referral Selected");
  }, []);

  const referralCodeName = `${qUserData.data?.profile.firstName}${qUserData.data?.profile.lastName}`.replace(
    new RegExp("[^a-zA-Z]", "g"),
    ""
  );

  let referralCode =
    customReferralCode || qUserData.data?.tag || referralCodeName;

  return (
    <>
      <WSText.Heading3>Share Wingspan</WSText.Heading3>
      <WSText.Paragraph mt="M" color="grey600">
        Know someone who might benefit from Wingspan? Refer them!
      </WSText.Paragraph>

      <WSText.Label color="grey600" mt="S">
        Copy link
      </WSText.Label>
      <WSCopyText
        onSuccess={() => {
          track("Referral Link Copied");
        }}
        isURL
        shareTitle="Referral Link"
        mt="XS"
        data={`http://www.wingspan.app/?rc=${referralCode}`}
      />

      <WSText.Label color="grey600" mt="S">
        Send invitation by email
      </WSText.Label>

      <WSTextInput
        mt="XS"
        key="email"
        name="email"
        value={email}
        error={isFocused && !!error}
        onChange={event => {
          setEmail(event.target.value);
        }}
        placeholder="Email address"
        postfix={
          <a
            href={`mailto:${email}?cc=referral@wingspan.app&subject=Join%20me%20on%20Wingspan&body=${getMailtoBody(
              qUserData.data?.tag || userId
            )}`}
            onClick={event => {
              setIsFocused(true);

              if (error) {
                event.preventDefault();
              } else {
                track("Referral Shared", {
                  channel: "email",
                  invitee_email: email
                });
              }
            }}
          >
            <WSButton small className={styles.mailButton} type="button">
              <WSIcon name="mail" />
            </WSButton>
          </a>
        }
      />

      <WSButton.Tertiary
        mt="L"
        onClick={() => {
          closeModal(REFERRALS_MODAL);
        }}
        fullWidth
        type="button"
      >
        Cancel
      </WSButton.Tertiary>
    </>
  );
};

export const ModalReferrals: React.FC = () => (
  <WSModal name={REFERRALS_MODAL} size="S" key="modal">
    {props => <ModalContent {...props} />}
  </WSModal>
);
